import { Component, HostBinding, Input } from '@angular/core';

/** Paleta admitida para el filo y el tinte de la tarjeta. */
export type StatColor = 'accent' | 'teal' | 'magenta' | 'purple' | 'green' | 'red' | 'blue';

/** Sentido de la variación contra el periodo anterior. */
export type StatTrend = 'up' | 'down' | 'flat';

/** Cada color de la API se resuelve a su token; nunca a un literal. */
const COLOR_TOKENS: Record<StatColor, string> = {
  accent:  'var(--z-accent)',
  blue:    'var(--z-blue)',
  green:   'var(--z-green)',
  magenta: 'var(--z-magenta)',
  purple:  'var(--z-purple)',
  red:     'var(--z-red)',
  teal:    'var(--z-teal)',
};

/**
 * Tarjeta de KPI de la banda superior de una sección.
 *
 * La piel (cristal, filo de color, tipografía de la cifra) vive en las clases
 * `.z-kpi-*` de styles.scss; aquí sólo se resuelven la composición y el estado
 * de la variación. Si llega `route`, la tarjeta entera se vuelve un enlace.
 */
@Component({
  selector: 'app-stat-card',
  standalone: false,
  templateUrl: './stat-card.component.html',
  styleUrl: './stat-card.component.scss',
})
export class StatCardComponent {

  @Input() public label!: string;
  @Input() public value!: string | number;
  @Input() public suffix = '';
  @Input() public color: StatColor = 'accent';
  /** Variación vs. el periodo anterior. Ausente = no se pinta la pastilla. */
  @Input() public delta?: number;
  @Input() public hint?: string;
  @Input() public icon?: string;
  /** Si viene, la tarjeta navega a esta ruta. */
  @Input() public route?: string;

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR COLOR                                */
  /* -------------------------------------------------------------------------- */

  /** El color se publica en el host y la tarjeta lo hereda (ver el SCSS). */
  @HostBinding('style.--z-kpi-color')
  public get colorToken(): string {
    return COLOR_TOKENS[this.color] ?? COLOR_TOKENS.accent;
  }

  /* -------------------------------------------------------------------------- */
  /*                                ANCHOR DELTA                                */
  /* -------------------------------------------------------------------------- */

  public get hasDelta(): boolean {
    return this.delta !== undefined && this.delta !== null && !Number.isNaN(this.delta);
  }

  public get trend(): StatTrend {
    if (!this.hasDelta || this.delta === 0) {
      return 'flat';
    }

    return (this.delta as number) > 0 ? 'up' : 'down';
  }

  /** La flecha acompaña siempre al color: el color solo nunca es indicador. */
  public get trendIcon(): string {
    switch (this.trend) {
      case 'up':   return 'bi-arrow-up';
      case 'down': return 'bi-arrow-down';
      default:     return 'bi-dash';
    }
  }

  public get deltaText(): string {
    const value = this.delta ?? 0;

    return value > 0 ? `+${value}` : `${value}`;
  }

  /** Lo que lee el lector de pantalla junto a la cifra de variación. */
  public get trendLabel(): string {
    switch (this.trend) {
      case 'up':   return 'sube respecto al periodo anterior';
      case 'down': return 'baja respecto al periodo anterior';
      default:     return 'sin cambio respecto al periodo anterior';
    }
  }

  public get hasFoot(): boolean {
    return this.hasDelta || !!this.hint || !!this.route;
  }
}
